import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CycleFragment,
  PerformanceCyclesUpdateCycleDocument,
  PerformanceCyclesUpdateCycleMutationVariables,
} from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { validateDate, validateTitle } from 'modules/performance_cycles/lib/cyclesValidations'

export const UpdateCycleModal: React.FC<{
  cycle: CycleFragment
  onClose: () => void
}> = ({ cycle, onClose }) => {
  const [updateCycle] = useMutation(PerformanceCyclesUpdateCycleDocument, {
    extractorFn: (d) => d.performanceCyclesMutations.updatePerformancecyclesCycle,
  })

  const form = useForm<PerformanceCyclesUpdateCycleMutationVariables>({
    defaultValues: {
      id: cycle.id,
      startOn: cycle.startOn,
      endOn: cycle.endOn,
      title: cycle.title,
    },
    validate(values) {
      const errors: FormErrors<PerformanceCyclesUpdateCycleMutationVariables> = {}

      const titleError = validateTitle(values.title)
      if (titleError) {
        errors.title = titleError
      }

      const dateError = validateDate(values.startOn, values.endOn)
      if (dateError) {
        errors.endOn = dateError
      }

      return errors
    },
    onSubmit: async (values) => {
      const result = await updateCycle({
        variables: values,
      })

      const handled = handleResult(result, () => null)
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('performance_cycles.edit_cycle_modal.title')}
      onClose={onClose}
      illustration="time-planning"
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button onClick={() => onClose()} type="secondary" disabled={form.isSubmitting}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s12">
        <TextField
          error={form.errors.title?.toString()}
          label={i18n.t('performance_goals.create_key_results_modal.fields.title')}
          value={form.values.title}
          onChange={(value) => form.setFieldValue('title', value)}
        />
        <Box flexDirection="row" gap="s12">
          <DayPicker
            value={form.values.startOn}
            format="ui"
            valueFormat="backend"
            label={i18n.t('performance_goals.create_key_results_modal.fields.start_on')}
            mode="single"
            onChange={(date) => {
              form.setValues({ ...form.values, startOn: date })
            }}
          />
          <DayPicker
            value={form.values.endOn}
            error={form.errors.endOn?.toString()}
            format="ui"
            valueFormat="backend"
            label={i18n.t('performance_goals.create_key_results_modal.fields.end_on')}
            mode="single"
            onChange={(date) => {
              form.setValues({ ...form.values, endOn: date })
            }}
          />
        </Box>
      </Box>
    </Modal>
  )
}
